import React, { useState } from 'react';
import { formatVND } from '../utils/priceFormatter';

interface Course {
  id: string;
  name: string;
  price: string;
  discountPrice?: string;
  endDiscountDate?: string;
}

interface CourseRegistrationFormProps {
  course: Course;
  user: {
    name: string;
    email: string;
  };
  onSubmit: (data: { name: string; email: string; courseId: string }) => Promise<void>;
  onCancel: () => void;
}

const CourseRegistrationForm: React.FC<CourseRegistrationFormProps> = ({
  course,
  user,
  onSubmit,
  onCancel
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Check if discount is valid
  const isDiscountValid = () => {
    if (!course.discountPrice || !course.endDiscountDate) {
      return false;
    }
    const endDate = new Date(course.endDiscountDate).getTime();
    const now = new Date().getTime();
    return endDate > now;
  };

  const displayPrice = isDiscountValid() ? course.discountPrice : course.price;
  const hasDiscount = isDiscountValid();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    setIsSubmitting(true);

    try {
      await onSubmit({
        name: user.name,
        email: user.email,
        courseId: course.id
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div style={{
        padding: '40px 24px',
        textAlign: 'center'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          margin: '0 auto 20px',
          borderRadius: '50%',
          backgroundColor: '#10b981',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
          </svg>
        </div>
        <h3 style={{
          margin: '0 0 12px 0',
          fontSize: '24px',
          fontWeight: '600',
          color: '#1f2937'
        }}>
          Registration Submitted!
        </h3>
        <p style={{
          margin: '0 0 24px 0',
          fontSize: '16px',
          color: '#6b7280',
          lineHeight: '1.6'
        }}>
          Thank you for registering! We've sent a confirmation email with payment instructions to <strong>{user.email}</strong>.
        </p>
        <button
          onClick={onCancel}
          style={{
            padding: '12px 24px',
            backgroundColor: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#5568d3';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#667eea';
          }}
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{
      padding: '32px'
    }}>
      {/* Course Information */}
      <div style={{
        marginBottom: '32px'
      }}>
        <h3 style={{
          margin: '0 0 20px 0',
          fontSize: '14px',
          fontWeight: '600',
          color: '#9CA3AF',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          Course Details
        </h3>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: '20px',
          alignItems: 'start',
          padding: '20px',
          backgroundColor: '#F9FAFB',
          borderRadius: '16px',
          border: '1px solid #F3F4F6'
        }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '500',
              color: '#6B7280',
              marginBottom: '4px'
            }}>
              Selected Course
            </label>
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#111827'
            }}>
              {course.name}
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '500',
              color: '#6B7280',
              marginBottom: '4px'
            }}>
              Price
            </label>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end'
            }}>
              {hasDiscount ? (
                <>
                  <span style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#EF4444'
                  }}>
                    {formatVND(course.discountPrice)}
                  </span>
                  <span style={{
                    textDecoration: 'line-through',
                    color: '#9CA3AF',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}>
                    {formatVND(course.price)}
                  </span>
                </>
              ) : (
                <span style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#111827'
                }}>
                  {formatVND(course.price)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* User Information */}
      <div style={{
        marginBottom: '32px'
      }}>
        <h3 style={{
          margin: '0 0 20px 0',
          fontSize: '14px',
          fontWeight: '600',
          color: '#9CA3AF',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          Personal Information
        </h3>

        <div style={{ display: 'grid', gap: '20px' }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Full Name
            </label>
            <input
              type="text"
              value={user.name}
              readOnly
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '15px',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                backgroundColor: '#F9FAFB',
                color: '#111827',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#6366F1';
                e.target.style.backgroundColor = 'white';
                e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#E5E7EB';
                e.target.style.backgroundColor = '#F9FAFB';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Email Address
            </label>
            <input
              type="email"
              value={user.email}
              readOnly
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '15px',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                backgroundColor: '#F9FAFB',
                color: '#111827',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#6366F1';
                e.target.style.backgroundColor = 'white';
                e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#E5E7EB';
                e.target.style.backgroundColor = '#F9FAFB';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '16px',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: '12px',
          marginBottom: '24px',
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <div style={{ color: '#B91C1C', fontSize: '14px', lineHeight: '1.5' }}>
            {error}
          </div>
        </div>
      )}

      {/* Submit Buttons */}
      <div style={{
        display: 'flex',
        gap: '16px',
        justifyContent: 'flex-end',
        paddingTop: '24px',
        borderTop: '1px solid #F3F4F6'
      }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          style={{
            padding: '12px 24px',
            backgroundColor: 'white',
            color: '#4B5563',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: isSubmitting ? 0.6 : 1
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.backgroundColor = '#F9FAFB';
              e.currentTarget.style.borderColor = '#D1D5DB';
              e.currentTarget.style.color = '#111827';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = '#E5E7EB';
              e.currentTarget.style.color = '#4B5563';
            }
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            padding: '12px 28px',
            background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: isSubmitting ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)'
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(79, 70, 229, 0.3)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 70, 229, 0.2)';
            }
          }}
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeOpacity="0.25" />
                <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Processing...
            </>
          ) : (
            <>
              Confirm Registration
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default CourseRegistrationForm;

